import { useEffect, useRef, useState } from "react";
import { EuiBasicTable, EuiBasicTableColumn, EuiCheckbox } from "@elastic/eui";
import { Resource } from "../../profileTypes";
import BadgeCount from "components/Badge/BadgeCount";
import Badge from "components/Badge/Badge";
import IconButton from "components/Buttons/IconButton";
import { useNewProfileResourcesListStyles, useProfileStyles } from "../../profileStyles";

interface NewProfileResourcesListProps {
  selectedResources: Resource[];
  changeSelectedResources: (instances: Resource[]) => void;
  isSystemProfile?: boolean;
}

const ProfileResourcesList = ({
  selectedResources,
  changeSelectedResources,
  isSystemProfile,
}: NewProfileResourcesListProps) => {
  const classes = useNewProfileResourcesListStyles();
  const sharedClasses = useProfileStyles();
  const [sortField, setSortField] = useState<keyof Resource>("label");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const tableKey = useRef(0);

  useEffect(() => {
    tableKey.current += 1;
  }, [selectedResources]);
  const tableHeight = selectedResources.length > 8 ? "320px" : "auto";

  const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isSystemProfile) return;

    const isChecked = e.target.checked;
    const updatedResources = selectedResources.map((resource) => ({
      ...resource,
      checked: isChecked ? ("on" as "on") : undefined,
    }));

    changeSelectedResources(updatedResources);
  };

  const handleDeleteResource = (resourceKey: string) => {
    if (isSystemProfile) return;

    const updatedResources = selectedResources.filter((resource) => resource.key !== resourceKey);
    changeSelectedResources(updatedResources);
  };

  const ResourceHeader = () => {
    if (selectedResources.length === 0) {
      return <span style={{ fontWeight: 600, fontSize: "var(--fs10)" }}>Resource</span>;
    } else {
      return (
        <div className={sharedClasses.selectText}>
          <span style={{ fontWeight: 600, fontSize: "var(--fs10)" }}>Resources</span>{" "}
          <BadgeCount number={selectedResources.length} />
        </div>
      );
    }
  };

  const columns: Array<EuiBasicTableColumn<Resource>> = [
    {
      name: (
        <EuiCheckbox
          id="select-all-resources-checkbox"
          checked={
            selectedResources.length > 0 &&
            selectedResources.every((resource) => resource.checked === "on")
          }
          disabled={isSystemProfile}
          onChange={handleSelectAllChange}
          aria-label="Select all resources"
        />
      ),
      width: "50px",
      render: (resource: Resource) => (
        <EuiCheckbox
          id={`table_checkbox_${resource.key}`}
          checked={resource.checked === "on"}
          disabled={isSystemProfile}
          onChange={(e) => {
            const updatedResources = selectedResources.map((r) =>
              r.key === resource.key
                ? { ...r, checked: e.target.checked ? ("on" as "on") : undefined }
                : r
            );
            changeSelectedResources(updatedResources);
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },

    {
      field: "label",
      name: <ResourceHeader />,
      sortable: true,
      truncateText: false,
    },
    {
      field: "resourceType",
      name: "Resource Type",
      sortable: true,
      truncateText: false,
      render: (v: string) => <Badge text={v} style={{ borderRadius: "4px" }} />,
    },
    {
      name: "",
      align: "right",
      width: "40px",
      render: (resource: Resource) => (
        <IconButton
          type="trash"
          onClick={() => handleDeleteResource(resource.key)}
          disabled={isSystemProfile}
        />
      ),
    },
  ];

  return (
    <div
      style={{
        maxHeight: tableHeight,
        overflow: "hidden",
        //overflow: selectedResources.length > 8 ? "auto" : "visible",
        borderRadius: "10px",
        border: "1px solid var(--border-color)",
      }}>
      <EuiBasicTable
        key={tableKey.current}
        items={selectedResources}
        itemId="key"
        columns={columns}
        rowHeader="label"
        className={classes.table}
        noItemsMessage="No resources assigned"
        sorting={{
          sort: {
            field: sortField,
            direction: sortDirection,
          },
        }}
        onChange={({ sort }) => {
          if (sort) {
            setSortField(sort.field);
            setSortDirection(sort.direction);

            const sorted = [...selectedResources].sort((a, b) => {
              const aValue = a[sort.field];
              const bValue = b[sort.field];
              const modifier = sort.direction === "asc" ? 1 : -1;
              // @ts-ignore
              return aValue < bValue ? -modifier : modifier;
            });

            changeSelectedResources(sorted);
          }
        }}
      />
    </div>
  );
};

export default ProfileResourcesList;
